import React from 'react'
import { graphql } from 'relay-runtime'
import styled from '@emotion/styled'
import { useFragment, useMutation } from 'react-relay'
import { AddComment$key } from './__generated__/AddComment.graphql'
import { SubmitHandler, useForm } from 'react-hook-form'
import { AddCommentCreateCommentMutation } from './__generated__/AddCommentCreateCommentMutation.graphql'
import { toast } from 'react-toastify'
import FormTextField from '../../components/FormTextField'
import { Button } from '@mui/material'
import { useAppDispatch } from '../../state/hooks'
import { setCreativeDrawerOpen } from '../../state/appSlice'
import { useGlobalState } from '../../state/v2/store'
import { useTrackingContext } from '../../analytics/useTracking'
import { trackCommentAdded } from '../../analytics/tracking'

interface AddCommentProps {
  fragmentRequest: AddComment$key
  metaAdId: string
}

export const addCommentFragment = graphql`
  fragment AddComment on User {
    picture
    fullName
  }
`

const createCommentMutation = graphql`
  mutation AddCommentCreateCommentMutation($input: CreateCommentRequest!) {
    createComment(input: $input) {
      id
      metaAd {
        id
        ...CreativeDrawerMetaAd
      }
    }
  }
`

const S = {
  Wrapper: styled.form`
    width: 20rem;
    margin: 1rem 0;
  `,
  Comment: styled.div`
    display: flex;
    & > :last-child {
      flex-grow: 1;
    }
  `,
  Image: styled.img`
    border-radius: 50%;
    height: 2.5rem;
    margin-right: 1rem;
  `,
  Actions: styled.div`
    display: flex;
    justify-content: space-between;
    margin: 1rem 0 0 3.25rem;
  `,
}

interface AddCommentForm {
  text: string
}

const AddComment = ({ fragmentRequest, metaAdId }: AddCommentProps) => {
  const user = useFragment(addCommentFragment, fragmentRequest)
  const form = useForm({ defaultValues: { text: '' } })
  const dispatch = useAppDispatch()
  const ctx = useTrackingContext()
  const brandId = useGlobalState((s) => s.brand?.[0]?.id ?? '')
  const [createCommentRequest, createCommentLoading] =
    useMutation<AddCommentCreateCommentMutation>(createCommentMutation)

  const onSubmit: SubmitHandler<AddCommentForm> = (values) => {
    const onCompleted = () => {
      toast.success('Successfully created comment.')
      trackCommentAdded({ ad_id: metaAdId, brand_id: brandId, account_id: ctx.accountId }, ctx)
      form.reset({ text: '' })
    }
    const onError = (error: Error) => {
      console.log(error)
      toast.error('Unable to create a comment.')
    }
    createCommentRequest({
      variables: {
        input: {
          metaAdId,
          text: values.text,
        },
      },
      onCompleted,
      onError,
    })
  }

  const cancel = () => {
    form.reset({})
    dispatch(setCreativeDrawerOpen(false))
  }

  return (
    <>
      <S.Wrapper onSubmit={form.handleSubmit(onSubmit)}>
        <S.Comment>
          <S.Image
            referrerPolicy="no-referrer"
            src={user.picture!}
            alt={user.fullName!}
          />
          <FormTextField
            autoFocus
            multiLine
            useForm={form}
            label="Add Comment"
            name="text"
            rules={{ required: true }}
          />
        </S.Comment>
        <S.Actions>
          <Button
            disabled={createCommentLoading}
            variant="outlined"
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button
            disabled={createCommentLoading}
            type="submit"
            variant="contained"
          >
            Comment
          </Button>
        </S.Actions>
      </S.Wrapper>
    </>
  )
}

export default AddComment
